import React from 'react';
import { Badge } from '../../../ui/Badge';
import TooltipV2 from 'app/shared/modules/TooltipV2';
import * as styles from './unitsModule.styles';

type Unit = {
  modelId: string;
  name: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  isNewUnit?: boolean;
  createdAgo?: string;
  priceDrop?: number;
};

type Floorplan = {
  modelId: string;
  name: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  photos: Array<{ url: string }>;
  largePhotos: Array<{ url: string }>;
  layoutCount?: number;
  units: Array<Unit>;
};

type UnitTableProps = {
  units: Array<Unit>;
  floorplan: Floorplan;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
};

const formatRent = (low: number): string => {
  if (!low || low === 0) return 'Contact';
  return `$${low.toLocaleString('en-US')}`;
};

const formatSqft = (sqft: number): string => {
  if (!sqft || sqft === 0) return '—';
  return sqft.toLocaleString('en-US');
};

const formatAvailability = (date: string): string => {
  if (!date) return '—';
  const lower = date.toLowerCase();
  if (lower === 'now' || lower === 'available now' || lower === 'available') return 'Now';
  return date;
};

const formatFloorplanRent = (floorplan: Floorplan): string => {
  const { low, high } = floorplan;
  if (!low && !high) return '—';
  const formattedLow = `$${low.toLocaleString('en-US')}`;
  if (!high || high === low) return formattedLow;
  return `${formattedLow} - $${high.toLocaleString('en-US')}`;
};

export const UnitTable = ({ units, floorplan, onUnitSelect }: UnitTableProps) => {
  const hasUnits = units && units.length > 0;

  return (
    <>
      {hasUnits && <h4 className={styles.unitTableHeading}>Unit with the same floor plan</h4>}
      <table className={styles.unitTable}>
        <thead>
          <tr>
            <th className={styles.unitTableHeader}>Unit</th>
            <th className={styles.unitTableHeader}>Rent</th>
            <th className={styles.unitTableHeader}>Sqft</th>
            <th className={styles.unitTableHeader}>Available</th>
          </tr>
        </thead>
        <tbody>
          {hasUnits ? (
            units.map((unit) => (
              <tr
                key={unit.modelId || unit.name}
                className={styles.unitTableRow}
                onClick={() => onUnitSelect(unit, floorplan)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onUnitSelect(unit, floorplan);
                  }
                }}
              >
                <td className={styles.unitNameCell}>
                  <span className={styles.unitNameCellContent}>
                    <span className={styles.unitNameText}>{unit.name}</span>
                    {unit.priceDrop ? (
                      <span
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <TooltipV2
                          content={`$${unit.priceDrop.toLocaleString('en-US')} price drop`}
                          position="below"
                          size="auto"
                          positionStrategy="trigger-relative"
                        >
                          <Badge
                            size="sm"
                            tone="alert"
                            aria-label={`$${unit.priceDrop.toLocaleString('en-US')} price drop`}
                          >
                            Price drop
                          </Badge>
                        </TooltipV2>
                      </span>
                    ) : (
                      unit.isNewUnit && (
                        <span
                          role="presentation"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <TooltipV2
                            content={unit.createdAgo ? `Added ${unit.createdAgo}` : 'Recently added'}
                            position="below"
                            size="auto"
                            positionStrategy="trigger-relative"
                          >
                            <Badge
                              size="sm"
                              aria-label={`New unit${unit.createdAgo ? ` - Added ${unit.createdAgo}` : ''}`}
                            >
                              New
                            </Badge>
                          </TooltipV2>
                        </span>
                      )
                    )}
                  </span>
                </td>
                <td className={styles.unitTableCell}>{formatRent(unit.low)}</td>
                <td className={styles.unitTableCell}>{formatSqft(unit.sqft)}</td>
                <td className={styles.unitTableCell}>{formatAvailability(unit.availabilityDate)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td className={styles.unitTableCell}>—</td>
              <td className={styles.unitTableCell}>{formatFloorplanRent(floorplan)}</td>
              <td className={styles.unitTableCell}>{formatSqft(floorplan.sqft)}</td>
              <td className={styles.unitTableCell}>—</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};

export type { Unit, Floorplan };
